import { Box, Container, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackIcon from "assets/icons/back.svg";
import LocalStorage from "utils/localstorage.util";
import {
  isYeloCheckoutSession,
  PARTNER_CHECKOUT_STORAGE_KEY,
  withPartnerToken,
} from "utils/partnerSession.util";
import { useAppDispatch } from "redux/hooks";
import { paymentActions } from "redux/slices/payment";
import PartnerPayfortCheckout from "./PartnerPayfortCheckout";
import PartnerPaymentSuccess from "./PartnerPaymentSuccess";

type PartnerCheckoutPayload = {
  trip_id: string;
  price?: number;
  currency?: string;
  payfort_form?: string;
  client_secret?: string;
  form?: string;
};

export default function PaymentForm() {
  const { t, i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [payload, setPayload] = useState<PartnerCheckoutPayload | null>(null);

  const paymentStatus = searchParams.get("type");
  const bookingId = searchParams.get("bookingID");
  const errorMessage = searchParams.get("message");
  const orderId = searchParams.get("order");

  useEffect(() => {
    if (!isYeloCheckoutSession()) {
      navigate("/not-authorize", { replace: true });
      return;
    }

    const stored = LocalStorage.getItem(PARTNER_CHECKOUT_STORAGE_KEY) as
      | PartnerCheckoutPayload
      | null;

    if (stored?.trip_id && (!orderId || stored.trip_id === orderId)) {
      setPayload(stored);
      if (stored.price != null) {
        dispatch(
          paymentActions.setPrice({
            price: stored.price,
            currency: stored.currency || "SAR",
          })
        );
      }
      return;
    }

    if (paymentStatus === "success" || paymentStatus === "error") return;

    navigate(
      isYeloCheckoutSession()
        ? withPartnerToken("/search-form#checkout")
        : "/search-form#checkout",
      { replace: true }
    );
  }, [dispatch, navigate, orderId, paymentStatus]);

  const payfortForm =
    payload?.payfort_form || payload?.client_secret || payload?.form || "";

  if (paymentStatus === "success") {
    const id = bookingId || orderId;
    if (!id) {
      return (
        <Box sx={{ minHeight: "calc(100vh - 79px)", bgcolor: "#F2F2F7", py: 6 }}>
          <Container maxWidth="sm">
            <Stack spacing={2} alignItems="center" textAlign="center">
              <Typography variant="h5" fontWeight={600} color="#520A76">
                {t("paymentSuccessful", "Payment successful")}
              </Typography>
              <Typography color="text.secondary">
                {errorMessage || t("bookingConfirmed", "Your booking is confirmed.")}
              </Typography>
            </Stack>
          </Container>
        </Box>
      );
    }
    return <PartnerPaymentSuccess bookingId={id} />;
  }

  if (paymentStatus === "error") {
    return (
      <Box sx={{ minHeight: "calc(100vh - 79px)", bgcolor: "#F2F2F7", py: 6 }}>
        <Container maxWidth="sm">
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography variant="h5" fontWeight={600} color="error">
              {t("paymentFailed", "Payment failed")}
            </Typography>
            <Typography color="text.secondary">
              {errorMessage || t("tryAgain", "Please try again.")}
            </Typography>
          </Stack>
        </Container>
      </Box>
    );
  }

  if (!payload || !payfortForm) {
    return (
      <Box sx={{ minHeight: "calc(100vh - 79px)", bgcolor: "#F2F2F7", py: 6 }}>
        <Typography textAlign="center" color="text.secondary">
          {t("loadingPayment", "Loading payment...")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "calc(100vh - 79px)", bgcolor: "#F2F2F7" }}>
      <Box className="form-header" sx={{ mb: 1, pt: 1 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" gap={1}>
            <img
              src={BackIcon}
              alt="<"
              width="40px"
              height="40px"
              style={{ cursor: "pointer" }}
              className={i18n.language === "ar" ? "rotate" : ""}
              onClick={() =>
                navigate(
                  isYeloCheckoutSession()
                    ? withPartnerToken("/search-form#checkout")
                    : "/search-form#checkout"
                )
              }
            />
            <Typography
              width="100%"
              fontWeight={600}
              fontSize="16px"
              color="#520A76"
              textAlign="center"
              marginRight="46px"
            >
              {String(t("checkout", "Checkout"))}
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
        <Grid
          container
          alignItems="center"
          spacing={{ xs: 2, md: 8 }}
          direction={{ xs: "column-reverse", md: "row" }}
        >
          <Grid item md={8} xs={12}>
            <Box mt={{ xs: 2, md: 4 }}>
              <Typography fontSize="14px" fontWeight={300} color="#0A0A0E" mb={2}>
                {String(t("payThisAmount", "Pay this amount securely with your card."))}
              </Typography>
              <PartnerPayfortCheckout payForm={payfortForm} />
            </Box>
          </Grid>
          <Grid item md={4} xs={12}>
            <Box mt={{ xs: 2, md: 4 }} textAlign="center">
              <Typography fontSize="16px" fontWeight={400} color="#0A0A0E">
                {t("Amount", "Amount")}
              </Typography>
              <Typography
                fontSize="24px"
                fontWeight={700}
                color="#0A0A0E"
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                {Number(payload.price || 0).toFixed(2)} {payload.currency || "SAR"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
